import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { SocItemsService } from '../../../../services/patientmr/socItems.service';
import { notifyPatientChartUpdated } from '../../../../constants/navigationEvents';
import { useHasAnyPermission } from '../../../../hooks/usePermission';
import type { PermissionString } from '../../../../types/permissions';
import type { SocItemsResponse } from '../../../../dtos/patientmr/socItems.dto';
import { AdvanceDirectiveCard, EmergencyPlanCard, OtherServicesCard, ThstepsCard } from './ReadinessCards';
import type { ReadinessSave } from './ReadinessCards';

/** Same routes as the SOC checklist manual items (patientmr:phi:soc-checklist:*) */
const SOC_READ_PERMS = ['patientmr:phi:soc-checklist:read', 'patientmr:phi:soc-checklist:readall'] as PermissionString[];
const SOC_UPDATE_PERMS = ['patientmr:phi:soc-checklist:update', 'patientmr:phi:soc-checklist:updateall'] as PermissionString[];

export interface ClinicalReadinessCardsProps {
  patientId: string;
  /** For the OOH-DNR minor rule (HSC §166.085); the server enforces it too */
  patientDob?: string;
  /** Discharged chart (DSC-07) */
  readOnly?: boolean;
}

/**
 * Clinical readiness records (Phase 19 F19): advance directive, emergency plan details, THSteps
 * and other services. Saved through PUT /patientmr/{patientId}/soc-items/{item}.
 */
const ClinicalReadinessCards: React.FC<ClinicalReadinessCardsProps> = ({ patientId, patientDob, readOnly = false }) => {
  const canRead = useHasAnyPermission(SOC_READ_PERMS);
  const canEdit = useHasAnyPermission(SOC_UPDATE_PERMS) && !readOnly;
  const [record, setRecord] = useState<SocItemsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRecord(await SocItemsService.get(patientId));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to load the readiness records.');
    }
  }, [patientId]);

  useEffect(() => {
    if (canRead && patientId) void load();
  }, [canRead, patientId, load]);

  const save: ReadinessSave = async (item, input) => {
    const next = await SocItemsService.update(patientId, item, input);
    setRecord(next);
    // The header OOH-DNR banner and checklist read the same record
    notifyPatientChartUpdated(patientId);
  };

  if (!canRead || !patientId) return null;

  return (
    <section aria-label="Clinical readiness" className="mt-4">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <ShieldCheck size={16} className="text-slate-600" />
        Clinical readiness
      </h3>
      {error && (
        <div role="alert" className="mb-2 flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <span>{error}</span>
          <button type="button" onClick={() => void load()} className="min-h-[44px] px-2 font-medium underline">Retry</button>
        </div>
      )}
      {!record && !error && <div className="h-24 animate-pulse rounded-lg bg-slate-100" />}
      {record && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <AdvanceDirectiveCard record={record} canEdit={canEdit} onSave={save} patientDob={patientDob} />
          <EmergencyPlanCard record={record} canEdit={canEdit} onSave={save} />
          <ThstepsCard record={record} canEdit={canEdit} onSave={save} />
          <OtherServicesCard record={record} canEdit={canEdit} onSave={save} />
        </div>
      )}
    </section>
  );
};

export default ClinicalReadinessCards;
